import React, { useEffect, useMemo, useState } from 'react';
import { useCampaign } from '../../contexts/CampaignContext';
import { useAuth } from '../../contexts/AuthContext';

// Fullscreen reaction modal — appears when a trap fires on the local player's
// token. Three seconds to choose Dodge / Jump / Brace / Sense; timeout = full
// damage. The dice broadcast + position shift + secondary-effect mitigation
// all happen in CampaignContext.submitTrapReaction.

const REACTIONS = [
  { id: 'dodge', label: 'Dodge', stat: 'dex', dcOffset: 0,  flavor: 'No damage; DM may shift you 1 cell.' },
  { id: 'jump',  label: 'Jump',  stat: 'str', dcOffset: 2,  flavor: 'No damage; lands 1 cell past the trap.' },
  { id: 'brace', label: 'Brace', stat: 'con', dcOffset: -3, flavor: 'Half damage; ignore secondary effect.' },
  { id: 'sense', label: 'Sense', stat: 'wis', dcOffset: 4,  flavor: 'No damage; stay exactly where you are.' },
];

function abilityMod(score) {
  return Math.floor(((score ?? 10) - 10) / 2);
}

const RESULT_SHOW_MS = 2200;

export default function TrapReactionPrompt() {
  const { trapReactions, traps, players, submitTrapReaction } = useCampaign();
  const { auth } = useAuth();
  const playerId = auth?.playerId;

  // Tick the clock so countdown / result-phase auto-dismiss stays live.
  // Always running while any reaction exists for this player.
  const [now, setNow] = useState(() => Date.now());
  const hasAny = playerId && Object.values(trapReactions || {}).some(
    (r) => r.ownerPlayerId === playerId
  );
  useEffect(() => {
    if (!hasAny) return;
    const id = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(id);
  }, [hasAny]);

  // Find the reaction for this player — keep it on screen briefly after
  // outcome resolves so the player sees a clear success/fail result.
  // NOT memoized: the "still within result window" check depends on `now`,
  // which ticks every 100ms. Memoizing here would cache past the window.
  const reaction = playerId ? Object.values(trapReactions || {}).find((r) => {
    if (r.ownerPlayerId !== playerId) return false;
    if (!r.outcome) return true; // button phase
    const since = now - (r.resolvedAt || 0);
    return since < RESULT_SHOW_MS;
  }) : null;

  // Auto-resolve on deadline (button phase).
  const [resolving, setResolving] = useState(false);
  useEffect(() => {
    if (!reaction || reaction.outcome || resolving) return;
    if (now >= reaction.deadlineTs) {
      setResolving(true);
      submitTrapReaction(reaction.id, null).finally(() => setResolving(false));
    }
  }, [reaction, now, submitTrapReaction, resolving]);

  if (!reaction) return null;

  const trap   = traps?.[reaction.trapId];
  const player = players?.[playerId];
  const baseDC = trap?.reactionDC ?? 12;
  const hasStats = !!player?.abilityScores;

  // ── Result phase ─────────────────────────────────────────────────────────
  if (reaction.outcome) {
    const { outcome, choice, rollTotal, damageTaken } = reaction;
    const reactionDef = REACTIONS.find((r) => r.id === choice);
    const isSuccess = outcome === 'success';
    const isTimeout = outcome === 'timeout';
    let headline, headlineColor, detail;
    if (isTimeout) {
      headline = '⏱ Too slow!';
      headlineColor = '#c0392b';
      detail = `Full damage: ${damageTaken ?? '—'}`;
    } else if (isSuccess) {
      headline = `🎉 ${reactionDef?.label} succeeded!`;
      headlineColor = '#4ade80';
      detail = damageTaken
        ? `Half damage: ${damageTaken}`
        : 'No damage — you escaped.';
    } else {
      headline = `💢 ${reactionDef?.label} missed!`;
      headlineColor = '#c0392b';
      detail = `Took ${damageTaken ?? '—'} damage.`;
    }
    const dc = baseDC + (reactionDef?.dcOffset ?? 0);
    return (
      <div style={styles.overlay}>
        <div style={{ ...styles.panel, borderColor: headlineColor, boxShadow: `0 20px 60px ${headlineColor}55` }}>
          <div style={{ ...styles.resultHeadline, color: headlineColor }}>{headline}</div>
          {!isTimeout && (
            <div style={styles.resultDetail}>
              Rolled <strong>{rollTotal}</strong> vs DC {dc}
            </div>
          )}
          <div style={styles.resultBig}>{detail}</div>
        </div>
      </div>
    );
  }

  // ── Button phase ─────────────────────────────────────────────────────────
  const msLeft = Math.max(0, reaction.deadlineTs - now);
  const secLeft = Math.ceil(msLeft / 1000);
  const ringPct = Math.min(1, msLeft / 3000);

  const pick = async (choice) => {
    if (resolving) return;
    setResolving(true);
    try {
      await submitTrapReaction(reaction.id, choice);
    } finally {
      setResolving(false);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.panel}>
        <div style={styles.header}>
          <span style={styles.title}>🪤 {trap?.label || 'Trap'}!</span>
          <span style={styles.timer}>{secLeft}s</span>
        </div>
        <div style={styles.subtitle}>
          Reaction DC {baseDC}{hasStats ? '' : ' — stats not configured, reactions roll flat'}
        </div>

        <div style={styles.ring}>
          <div style={{ ...styles.ringFill, transform: `scaleX(${ringPct})` }} />
        </div>

        <div style={styles.grid}>
          {REACTIONS.map((r) => {
            const score = player?.abilityScores?.[r.stat] ?? 10;
            const mod   = abilityMod(score);
            const dc    = baseDC + r.dcOffset;
            return (
              <button
                key={r.id}
                style={styles.btn}
                onClick={() => pick(r.id)}
                disabled={resolving}
              >
                <div style={styles.btnTop}>
                  <span style={styles.btnLabel}>{r.label}</span>
                  <span style={styles.btnStat}>{r.stat.toUpperCase()} {mod >= 0 ? `+${mod}` : mod}</span>
                </div>
                <div style={styles.btnDc}>vs DC {dc}</div>
                <div style={styles.btnFlavor}>{r.flavor}</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 100,
    background: 'rgba(0,0,0,0.75)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 16,
  },
  panel: {
    width: '100%', maxWidth: 520,
    background: 'linear-gradient(180deg, #2a1a1a, #1a1020)',
    border: '2px solid #c0392b',
    borderRadius: 8,
    boxShadow: '0 20px 60px rgba(192,57,43,0.5)',
    color: '#f0e6d0',
    fontFamily: "'Press Start 2P', monospace",
    padding: 18,
    display: 'flex', flexDirection: 'column', gap: 12,
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
  },
  title:    { fontSize: 22, fontWeight: 800, color: '#fbbf24', letterSpacing: '0.04em', textShadow: '0 0 20px rgba(192,57,43,0.7)' },
  timer:    { fontSize: 20, fontWeight: 800, color: '#fbbf24', fontVariantNumeric: 'tabular-nums' },
  subtitle: { fontSize: 12, color: '#9a8b6e', letterSpacing: '0.04em' },
  ring:     { height: 4, background: 'rgba(0,0,0,0.3)', borderRadius: 2, overflow: 'hidden' },
  ringFill: { height: '100%', background: 'linear-gradient(90deg, #fbbf24, #c0392b)', transformOrigin: 'left', transition: 'transform 0.1s linear' },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
  },
  btn: {
    display: 'flex', flexDirection: 'column', gap: 4,
    padding: '12px 12px',
    background: 'linear-gradient(180deg, #1a2a48, #0e1528)',
    border: '2px solid #4a3d28',
    borderRadius: 6,
    color: '#f0e6d0',
    fontFamily: "'Press Start 2P', monospace",
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'border-color 0.15s, transform 0.05s',
  },
  btnTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' },
  btnLabel: { fontSize: 16, fontWeight: 800, color: '#fbbf24', letterSpacing: '0.04em' },
  btnStat:  { fontSize: 11, fontWeight: 700, color: '#86efac', fontVariantNumeric: 'tabular-nums' },
  btnDc:    { fontSize: 11, color: '#9a8b6e', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' },
  btnFlavor:{ fontSize: 10, color: '#9a8b6e', lineHeight: 1.35, fontStyle: 'italic', fontFamily: 'system-ui, sans-serif' },

  resultHeadline: { fontSize: 28, fontWeight: 800, letterSpacing: '0.04em', textAlign: 'center', textShadow: '0 0 24px rgba(0,0,0,0.5)' },
  resultDetail:   { fontSize: 13, color: '#9a8b6e', textAlign: 'center', fontFamily: 'system-ui, sans-serif' },
  resultBig:      { fontSize: 18, fontWeight: 700, color: '#f0e6d0', textAlign: 'center', marginTop: 4 },
};
